import { inferUploadContentType } from './fileTypes.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

async function parseResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data.details?.join(', ') || data.error || `Request failed: ${response.status}`;
    throw new Error(message);
  }
  return data;
}

export async function enrichLocation(coords) {
  const response = await fetch(`${API_BASE_URL}/api/location/enrich`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      coords,
      capturedAt: new Date().toISOString()
    })
  });
  return parseResponse(response);
}

export async function requestSignedUpload({ idempotencyKey, category, file }) {
  const contentType = inferUploadContentType(file);
  const response = await fetch(`${API_BASE_URL}/api/uploads/signed-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      idempotencyKey,
      category,
      fileName: file.name,
      contentType
    })
  });
  return parseResponse(response);
}

export async function uploadFileToSignedUrl({ uploadUrl, file, contentType }) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType || file.type || 'application/octet-stream'
    },
    body: file
  });
  if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
}

export async function submitSurvey(payload) {
  const response = await fetch(`${API_BASE_URL}/api/submissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return parseResponse(response);
}

export async function getPublicSettings() {
  const response = await fetch(`${API_BASE_URL}/api/settings`);
  return parseResponse(response);
}

export async function adminLogin(payload) {
  const response = await fetch(`${API_BASE_URL}/api/admin/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return parseResponse(response);
}

export async function adminLogout() {
  const response = await fetch(`${API_BASE_URL}/api/admin/logout`, {
    method: 'POST',
    credentials: 'include'
  });
  return parseResponse(response);
}

export async function getAdminMe() {
  const response = await fetch(`${API_BASE_URL}/api/admin/me`, {
    credentials: 'include'
  });
  return parseResponse(response);
}

export async function getAdminSettings() {
  const response = await fetch(`${API_BASE_URL}/api/admin/settings`, {
    credentials: 'include'
  });
  return parseResponse(response);
}

export async function createAdminProject(payload) {
  const response = await fetch(`${API_BASE_URL}/api/admin/projects`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return parseResponse(response);
}

export async function updateAdminSettings(payload) {
  const response = await fetch(`${API_BASE_URL}/api/admin/settings`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return parseResponse(response);
}

function queryString(params = {}) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, value);
  }
  const value = search.toString();
  return value ? `?${value}` : '';
}

export async function getAdminSurveys(filters = {}) {
  const response = await fetch(`${API_BASE_URL}/api/admin/surveys${queryString(filters)}`, {
    credentials: 'include'
  });
  return parseResponse(response);
}

export async function getAdminSurveyStats(filters = {}) {
  const response = await fetch(`${API_BASE_URL}/api/admin/surveys/stats${queryString(filters)}`, {
    credentials: 'include'
  });
  return parseResponse(response);
}

export async function getAdminSurveyExportData(filters = {}) {
  const response = await fetch(`${API_BASE_URL}/api/admin/surveys/export-data${queryString(filters)}`, {
    credentials: 'include'
  });
  return parseResponse(response);
}

export async function getAdminSurvey(id) {
  const response = await fetch(`${API_BASE_URL}/api/admin/surveys/${encodeURIComponent(id)}`, {
    credentials: 'include'
  });
  return parseResponse(response);
}

export async function updateAdminSurveyDetails(id, payload) {
  const response = await fetch(`${API_BASE_URL}/api/admin/surveys/${encodeURIComponent(id)}/details`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return parseResponse(response);
}

export async function updateAdminSurveyStatus(id, status) {
  const response = await fetch(`${API_BASE_URL}/api/admin/surveys/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status })
  });
  return parseResponse(response);
}

export async function requestAdminSignedUpload({ id, category, file }) {
  const contentType = inferUploadContentType(file);
  const response = await fetch(`${API_BASE_URL}/api/admin/surveys/${encodeURIComponent(id)}/uploads/signed-url`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      category,
      fileName: file.name,
      contentType
    })
  });
  return parseResponse(response);
}

export async function addAdminSurveyFile(id, payload) {
  const response = await fetch(`${API_BASE_URL}/api/admin/surveys/${encodeURIComponent(id)}/files`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return parseResponse(response);
}

export async function updateAdminSurveyFileStatus(id, payload) {
  const response = await fetch(`${API_BASE_URL}/api/admin/surveys/${encodeURIComponent(id)}/files/status`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return parseResponse(response);
}

export async function getAdminUsers() {
  const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
    credentials: 'include'
  });
  return parseResponse(response);
}

export async function createAdminUser(payload) {
  const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return parseResponse(response);
}

export async function changeAdminPassword(payload) {
  const response = await fetch(`${API_BASE_URL}/api/admin/change-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return parseResponse(response);
}
